import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { Modal, Button, Collapse } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  MdContentCopy,
  MdKeyboardArrowDown,
  MdDeleteOutline,
  MdOutlineCheckCircle,
  MdOutlineRemoveCircleOutline,
  MdAddCircle,
  MdFileDownload,
} from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  Utils,
  fetchWithAuth,
  handleSessionExpired,
  getApiKeyDisplayStatus,
  filtersObjectHasAnyValue,
  API_KEY_STATUS,
  formatDateTime,
  can,
  exportListCsv,
} from "../../utils";
import ApiKeysFiltersBar from "./ApiKeysFiltersBar";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import Swal from "sweetalert2";

const EMPTY_API_KEY_FILTERS = { status: "" };

const ApiKeys = () => {
  const statusSelectOptions = useMemo(
    () =>
      (API_KEY_STATUS || []).map((e) => ({
        value: String(e.value),
        label: e.label,
      })),
    []
  );

  const { appId } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_API_KEY_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_API_KEY_FILTERS }));
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: () =>
      (can("settings.api_keys.change_status") ? 1 : 0) +
      (can("settings.api_keys.delete") ? 1 : 0),
  });

  useEffect(() => {
    const hasContent =
      filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
    if (!hasContent) {
      setFilterAccordionOpen(false);
      return;
    }
    setFilterAccordionOpen(true);
  }, [draftFilters, appliedFilters]);

  const load = useCallback(() => {
    if (!appId) return;
    setLoading(true);
    const body = { app_id: appId };
    const st = (appliedFilters.status || "").trim();
    if (st === "0" || st === "1") {
      body.status = parseInt(st, 10);
    }
    fetchWithAuth(Utils.baseurl + "application/client-api-keys/list", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLoading(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) setRows(result.data || []);
        else toast.error(result.message || "Failed to load API keys");
      })
      .catch(() => {
        setLoading(false);
        toast.error("Network error");
      });
  }, [appId, appliedFilters.status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
  };

  const handleResetFilters = () => {
    const cleared = { ...EMPTY_API_KEY_FILTERS };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
  };

  const handleCreate = () => {
    if (!appId) return;
    setCreating(true);
    fetchWithAuth(Utils.baseurl + "application/client-api-keys/create", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        name: newKeyName.trim() || "API Key",
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setCreating(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status && result.data?.token) {
          setAddOpen(false);
          setNewKeyName("");
          setReveal(result.data);
          load();
          toast.success("API key created");
        } else {
          toast.error(result.message || "Could not create key");
        }
      })
      .catch(() => {
        setCreating(false);
        toast.error("Network error");
      });
  };

  const changeStatus = async (row, nextStatus) => {
    if (!appId) return;
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text:
        nextStatus === 1
          ? "Activate this API key?"
          : "Deactivate this key? Mobile apps using it will stop working until you update them.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: nextStatus === 1 ? "#3085d6" : "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: nextStatus === 1 ? "Yes, activate" : "Yes, deactivate",
    });
    if (!confirm.isConfirmed) return;
    fetchWithAuth(Utils.baseurl + "application/client-api-keys/status", {
      method: "POST",
      body: JSON.stringify({ app_id: appId, key_id: row.id, status: nextStatus }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (handleSessionExpired(res, result)) return;
        if (result.status) {
          toast.success(result.message || "Status updated");
          load();
        } else toast.error(result.message || "Failed");
      })
      .catch(() => toast.error("Network error"));
  };

  const handleDelete = async (row) => {
    if (!appId) return;
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `"${row.name || "This key"}" will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    fetchWithAuth(Utils.baseurl + "application/client-api-keys/delete", {
      method: "POST",
      body: JSON.stringify({ app_id: appId, key_id: row.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (handleSessionExpired(res, result)) return;
        if (result.status) {
          toast.success(result.message || "Key deleted");
          load();
        } else toast.error(result.message || "Failed");
      })
      .catch(() => toast.error("Network error"));
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed")
    );
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const body = { app_id: appId };
      const st = (appliedFilters.status || "").trim();
      if (st === "0" || st === "1") {
        body.status = parseInt(st, 10);
      }
      const { status, message } = await exportListCsv("application/client-api-keys/list", body, "api-keys.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>API Keys</h1>
            <p className="text-muted mb-0" style={{ fontSize: 14, maxWidth: 640, marginTop: 8 }}>
              Send <code style={{ fontSize: 13 }}>Authorization: Bearer &lt;token&gt;</code> on secured routes such as{" "}
              <code style={{ fontSize: 13 }}>POST /client/:appId/users</code>,{" "}
              <code style={{ fontSize: 13 }}>POST /client/:appId/logout</code>, and other{" "}
              <code style={{ fontSize: 13 }}>/client/:appId/…</code> endpoints. Treat keys like passwords.
            </p>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={load}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("settings.api_keys.add") ? (
            <button type="button" className="add_app_btn btn_theme_primary" onClick={() => setAddOpen(true)}>
              <MdAddCircle />
              <span>Add key</span>
            </button>
            ) : null}
          </div>
        </div>

        <div
          className="alert alert-warning mb-3 py-3 px-3"
          style={{ borderRadius: "0.5rem", fontSize: 14 }}
          role="alert"
        >
          <strong>Security:</strong> Anyone with a valid key can call secured client endpoints for this app. Disable keys you
          no longer use.
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
            aria-controls="api-keys-filter-panel"
            id="api-keys-filter-accordion"
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown
              className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
              size={22}
              aria-hidden
            />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div
              className="list-page-filter-accordion-body"
              id="api-keys-filter-panel"
              role="region"
              aria-labelledby="api-keys-filter-accordion"
            >
              <ApiKeysFiltersBar
                draftFilters={draftFilters}
                setDraftFilters={setDraftFilters}
                onSearch={handleApplyFilters}
                onReset={handleResetFilters}
                statusSelectOptions={statusSelectOptions}
              />
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div className="table_scroll_wrapper" ref={tableScrollRef}>
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Created</th>
                      <th className="col-status" style={{ textAlign: "center" }}>
                        Status
                      </th>
                      <th className="col-action col-action-wide" style={{ textAlign: "right" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          No keys yet. Click <strong>Add key</strong> to create one. The full value is shown only once.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => {
                        const statusUi = getApiKeyDisplayStatus(r.status);
                        return (
                          <tr key={r.id}>
                            <td>{r.name}</td>
                            <td style={{ fontFamily: "ui-monospace, monospace" }}>{r.token_hint || "—"}</td>
                            <td className="text-muted">{r.createdAt ? formatDateTime(r.createdAt) : "—"}</td>
                            <td className="col-status" style={{ textAlign: "center" }}>
                              {statusUi.badgeClass ? (
                                <span className={statusUi.badgeClass}>{statusUi.label}</span>
                              ) : (
                                <span>{statusUi.label}</span>
                              )}
                            </td>
                            <td className="col-action col-action-wide" style={{ textAlign: "right" }}>
                              <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                                <button
                                  type="button"
                                  className="dropdown-trigger-btn btn btn-link p-0"
                                  aria-label="Actions"
                                  aria-expanded={openActionId === r.id}
                                  onClick={(e) => handleOpenDropdown(e, r.id, r)}
                                >
                                  <BsThreeDotsVertical />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={addOpen} onHide={() => !creating && setAddOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add API key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label">Name (optional)</label>
          <input
            className="form-control"
            placeholder="e.g. Production Android"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            maxLength={255}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAddOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating…" : "Generate key"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!reveal} onHide={() => setReveal(null)} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Save your API key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: "#b45309", fontSize: 14 }}>
            This is the only time the full key is shown. Copy it now and store it securely.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#f3f4f6",
              padding: "12px 14px",
              borderRadius: 8,
              wordBreak: "break-all",
              fontFamily: "ui-monospace, monospace",
              fontSize: 13,
            }}
          >
            <span style={{ flex: 1 }}>{reveal?.token}</span>
            <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => copyText(reveal?.token)} title="Copy">
              <MdContentCopy size={18} />
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setReveal(null)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>

      {openActionItem && dropdownPosition
        ? createPortal(
            <div
              ref={dropdownRef}
              style={{ position: "fixed", left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: 200 }}
            >
              <div className="dropdown-menu-subscription">
                {can("settings.api_keys.change_status") ? (
                <button
                  type="button"
                  onClick={() =>
                    handleDropdownAction(() =>
                      changeStatus(openActionItem, Number(openActionItem.status) === 1 ? 0 : 1)
                    )
                  }
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  {Number(openActionItem.status) === 1 ? (
                    <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  ) : (
                    <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: "0.875rem" }}>
                    {Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}
                  </span>
                </button>
                ) : null}
                {can("settings.api_keys.delete") ? (
                <button
                  type="button"
                  className="delete-class"
                  onClick={() => handleDropdownAction(() => handleDelete(openActionItem))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Delete</span>
                </button>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ApiKeys;
